import { objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default__default, { useState, useRef, useEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/quantity.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import { constraintValue, decrementValue, incrementValue } from './quantity-utils.js';
import { InputInfo, InputInfoChildren } from '../_atoms/input-info/input-info.js';
import Button from '../button/button.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const _excluded = ["active", "caption", "disabled", "error", "extraClasses", "info", "infoError", "inline", "id", "label", "max", "min", "minusBtnProps", "minusBtnTitle", "onBlur", "onChange", "onKeyDown", "plusBtnProps", "plusBtnTitle", "step", "value"],
  _excluded2 = ["onClick"],
  _excluded3 = ["onClick"];
const bem = HoneyBem.init('quantity', styles);
const Quantity = _ref => {
  let {
      active,
      caption,
      disabled,
      error,
      extraClasses,
      info,
      infoError,
      inline,
      id,
      label,
      max,
      min,
      minusBtnProps,
      minusBtnTitle,
      onBlur,
      onChange,
      onKeyDown,
      plusBtnProps,
      plusBtnTitle,
      step,
      value
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  const [val, setVal] = useState(value);
  const numberInput = useRef(null);

  // catching custom button onClick handlers
  // executing them alongside increment/decrement functionality
  const {
      onClick: minusBtnOnClick
    } = minusBtnProps,
    minusBtnRest = _objectWithoutProperties(minusBtnProps, _excluded2);
  const {
      onClick: plusBtnOnClick
    } = plusBtnProps,
    plusBtnRest = _objectWithoutProperties(plusBtnProps, _excluded3);

  // when user changes the value prop align the internal value
  // useState will be fired only on first render
  useEffect(() => {
    setVal(value);
  }, [value]);

  /**
   * Converts input value to integer if necessary and calls
   * setVal with the resulting value
   * @param newValue - number or string
   */
  const setValue = newValue => {
    const newVal = !Number.isNaN(newValue) ? parseInt(newValue, 10) : '';
    setVal(newVal);
  };

  /**
   * handles arrow up and arrow down keypress
   * - sets value with utils incremented or decremented value accordingly
   * - calls onKeyDown callback with input event object
   * @param e input event object
   */
  const handleKeyDown = e => {
    if (e.key === 'ArrowDown' || e.keyCode === 40) {
      // disabling native number input keyboard event handling to prevent value double increments
      e.preventDefault();
      setValue(decrementValue(val, step, min));
    }
    if (e.key === 'ArrowUp' || e.keyCode === 38 || e.key === ' ' || e.keyCode === 32) {
      // disabling native number input keyboard event handling to prevent value double increments
      e.preventDefault();
      setValue(incrementValue(val, step, max));
    }
    onKeyDown(e);
  };

  /**
   * handles input change
   * - sets state value with numeric version of user input value
   * - calls onChange callback with input event object
   * @param e - input event object
   */
  const handleInputChange = e => {
    setValue(e.target.value);
    onChange(e);
  };

  /**
   * handles input blur
   * - sets state with validated input value (numeric within min/max constraints)
   * - calls onBlur callback with input event object
   * @param e - input event object
   */
  const handleInputBlur = e => {
    setValue(constraintValue(e.target.value, min, max));
    onBlur(e);
  };

  /**
   * handles minus button click
   * @param e - input event onbject
   */
  const handleMinusClick = e => {
    setValue(decrementValue(val, step, min));
    if (typeof minusBtnOnClick === 'function') {
      minusBtnOnClick(e);
    }
  };

  /**
   * handles plus button click
   * @param e - input event onbject
   */
  const handlePlusClick = e => {
    setValue(incrementValue(val, step, max));
    if (typeof plusBtnOnClick === 'function') {
      plusBtnOnClick(e);
    }
  };
  return /*#__PURE__*/React__default__default.createElement("div", {
    className: bem.b([{
      inline,
      error,
      active,
      disabled
    }], extraClasses)
  }, label && /*#__PURE__*/React__default__default.createElement("label", {
    htmlFor: id,
    className: bem.e('label')
  }, label, caption && /*#__PURE__*/React__default__default.createElement("small", null, caption)), /*#__PURE__*/React__default__default.createElement("div", {
    className: bem.e('picker')
  }, /*#__PURE__*/React__default__default.createElement(Button, _extends({
    extraClasses: bem.e('button', 'minus'),
    disabled: disabled || val <= min,
    display: "square",
    onClick: handleMinusClick,
    title: minusBtnTitle,
    "aria-label": minusBtnTitle,
    "aria-controls": id
  }, minusBtnRest)), /*#__PURE__*/React__default__default.createElement("input", _extends({
    ref: numberInput,
    type: "number",
    role: "spinbutton",
    id: id,
    className: bem.e('input'),
    disabled: disabled,
    min: min,
    max: max,
    step: step,
    value: val,
    onKeyDown: handleKeyDown,
    onBlur: handleInputBlur,
    onChange: handleInputChange,
    "aria-live": "assertive"
  }, error && {
    'aria-invalid': true
  }, error && infoError && {
    'aria-errormessage': "infoError-".concat(id)
  }, info && {
    'aria-describedby': "info-".concat(id)
  }, rest)), /*#__PURE__*/React__default__default.createElement(Button, _extends({
    extraClasses: bem.e('button', 'plus'),
    disabled: disabled || val >= max,
    display: "square",
    onClick: handlePlusClick,
    title: plusBtnTitle,
    "aria-label": plusBtnTitle,
    "aria-controls": id
  }, plusBtnRest))), error && infoError && /*#__PURE__*/React__default__default.createElement(InputInfo, {
    error: true,
    id: id,
    className: bem.e('info')
  }, infoError), info && /*#__PURE__*/React__default__default.createElement(InputInfo, {
    id: id,
    className: bem.e('info')
  }, info));
};
Quantity.displayName = 'Quantity';
Quantity.defaultProps = {
  active: false,
  caption: null,
  disabled: false,
  error: false,
  extraClasses: '',
  info: null,
  infoError: null,
  inline: false,
  label: null,
  max: 100,
  min: 0,
  minusBtnProps: {},
  minusBtnTitle: 'Remove',
  onBlur: () => null,
  onChange: () => null,
  onKeyDown: () => null,
  plusBtnProps: {},
  plusBtnTitle: 'Add',
  step: 1,
  value: 0
};
Quantity.propTypes = {
  /** sets active state */
  active: PropTypes__default.bool,
  /** <small> caption text injected in <label>, this would not render if no label is provided */
  caption: PropTypes__default.string,
  /** Injection in className */
  extraClasses: PropTypes__default.string,
  /** Disables input and buttons */
  disabled: PropTypes__default.bool,
  /** sets error state */
  error: PropTypes__default.bool,
  /** Provides extra information for this input field */
  info: InputInfoChildren,
  /** Provides extra information for an error, gets displayed only when `error` is set to true */
  infoError: InputInfoChildren,
  /** Enforces label and input to appear in one line */
  inline: PropTypes__default.bool,
  /** Defines element ID that is used to bind it to the label and validation messages */
  id: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]).isRequired,
  /** Provides label text, required if aria-label is not passed */
  // we want label to be always checked against
  label(_ref2) {
    let {
      'aria-label': ariaLabel,
      label
    } = _ref2;
    // if no aria label nor label throw validation error
    return !ariaLabel && !label ? new Error('Either label or aria label are required') : null;
  },
  /** Maximum allowed value */
  max: PropTypes__default.number,
  /** Minimun allowed value */
  min: PropTypes__default.number,
  /** Rest props for minus button  */
  minusBtnProps: PropTypes__default.object,
  // eslint-disable-line
  /** Title for the minus button  */
  minusBtnTitle: PropTypes__default.string,
  /** Custom change event handler, gets event object from the input field */
  onChange: PropTypes__default.func,
  /** Custom blur event handler, gets event object from the input field */
  onBlur: PropTypes__default.func,
  /** Custom keyDown event handler, gets event object from the input field */
  onKeyDown: PropTypes__default.func,
  /** Rest props for plus button  */
  plusBtnProps: PropTypes__default.object,
  // eslint-disable-line
  /** Title for the plus button */
  plusBtnTitle: PropTypes__default.string,
  /** Increment/decrement amount */
  step: PropTypes__default.number,
  /** Input value */
  value: PropTypes__default.number
};
var quantity = Injecss(Quantity, styles);

export { Quantity, quantity as default };
